import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { triggerReportDownload } from '@components/ReportGenerator/utils/triggerReportDownload';
import LearnosityExamsEventEmitter, {
  ExamReportCsvCompletedPayload,
  ExamReportCsvFailedPayload,
  LearnosityExamsEvents,
  MessageFnType,
} from '../utils/LearnosityExamsEventEmitter';

export interface ExamReportCsvExportResult {
  isExporting: boolean;
  startExport: (queueExport: () => Promise<{ report_name: string }>) => Promise<void>;
}

const formatLabel = (extension: string): string =>
  extension.toLowerCase() === 'xlsx' ? 'Excel' : extension.toUpperCase();

type EarlyEvent =
  | { kind: 'completed'; payload: ExamReportCsvCompletedPayload }
  | { kind: 'failed'; payload: ExamReportCsvFailedPayload };

/**
 * Drives download buttons on exam report pages and PM Outcomes.
 *
 * The export runs in a Celery worker, so the request only queues the job. The
 * finished file's presigned URL arrives on the websocket, which is where the
 * download is actually triggered from.
 *
 * Fast workers can emit completion before the queue HTTP response returns
 * report_name. Early websocket events are buffered by report_name and replayed
 * once the queue response arrives.
 */
const useExamReportCsvExport = (
  fileExtension: string = 'csv',
): ExamReportCsvExportResult => {
  const [isExporting, setIsExporting] = useState(false);
  const pendingReportRef = useRef<string | null>(null);
  const awaitingQueueRef = useRef(false);
  const earlyEventsRef = useRef<Record<string, EarlyEvent>>({});
  const extensionRef = useRef(fileExtension);

  useEffect(() => {
    extensionRef.current = fileExtension;
  }, [fileExtension]);

  const clearPending = useCallback(() => {
    pendingReportRef.current = null;
    awaitingQueueRef.current = false;
    setIsExporting(false);
  }, []);

  const finishCompleted = useCallback(
    (completed: ExamReportCsvCompletedPayload) => {
      const extension = extensionRef.current;
      triggerReportDownload(
        completed.report_url,
        completed.report_name,
        extension,
      );
      toast.success(
        `Your ${formatLabel(extension)} export is ready and has started downloading.`,
      );
      clearPending();
    },
    [clearPending],
  );

  const finishFailed = useCallback(() => {
    toast.error(
      `${formatLabel(extensionRef.current)} export failed. Please try again.`,
    );
    clearPending();
  }, [clearPending]);

  useEffect(() => {
    const onCompleted: MessageFnType = (payload) => {
      const completed = payload as ExamReportCsvCompletedPayload;
      if (completed.report_name === pendingReportRef.current) {
        finishCompleted(completed);
        return;
      }
      // Queue response not back yet — remember this event for replay
      if (awaitingQueueRef.current) {
        earlyEventsRef.current[completed.report_name] = {
          kind: 'completed',
          payload: completed,
        };
      }
    };

    const onFailed: MessageFnType = (payload) => {
      const failed = payload as ExamReportCsvFailedPayload;
      if (failed.report_name === pendingReportRef.current) {
        finishFailed();
        return;
      }
      if (awaitingQueueRef.current) {
        earlyEventsRef.current[failed.report_name] = {
          kind: 'failed',
          payload: failed,
        };
      }
    };

    LearnosityExamsEventEmitter.on(
      LearnosityExamsEvents.EXAM_REPORT_CSV_COMPLETED,
      onCompleted,
    );
    LearnosityExamsEventEmitter.on(LearnosityExamsEvents.EXAM_REPORT_CSV_FAILED, onFailed);

    return () => {
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.EXAM_REPORT_CSV_COMPLETED,
        onCompleted,
      );
      LearnosityExamsEventEmitter.off(
        LearnosityExamsEvents.EXAM_REPORT_CSV_FAILED,
        onFailed,
      );
    };
  }, [finishCompleted, finishFailed]);

  const startExport = useCallback(
    async (queueExport: () => Promise<{ report_name: string }>) => {
      extensionRef.current = fileExtension;
      const label = formatLabel(fileExtension);
      awaitingQueueRef.current = true;
      setIsExporting(true);
      try {
        const { report_name: reportName } = await queueExport();
        pendingReportRef.current = reportName;
        awaitingQueueRef.current = false;
        toast.info(`Building your ${label} export. It will download when ready.`);

        const early = earlyEventsRef.current[reportName];
        if (early) {
          delete earlyEventsRef.current[reportName];
          if (early.kind === 'completed') {
            finishCompleted(early.payload);
          } else {
            finishFailed();
          }
        }
      } catch {
        toast.error(`Could not start the ${label} export. Please try again.`);
        earlyEventsRef.current = {};
        clearPending();
      }
    },
    [clearPending, fileExtension, finishCompleted, finishFailed],
  );

  return { isExporting, startExport };
};

export default useExamReportCsvExport;
